
import { sendFormDataWithAuth } from '@/utils/api';

export interface VoiceRequestParams {
  audioBlob: Blob;
  persona: string;
  sessionId: string;
  userId?: string;
  messageType: 'voice';
  personality?: string;
}

// Helper function to extract userId from Supabase auth token
const getUserIdFromSupabase = (): string | null => {
  try {
    // Try to get the auth token from Supabase localStorage
    const authTokenKey = Object.keys(localStorage).find(key => 
      key.includes('auth-token') && key.includes('sb-')
    );
    
    if (authTokenKey) {
      const authData = localStorage.getItem(authTokenKey);
      if (authData) {
        const parsed = JSON.parse(authData);
        // Extract user ID from the auth token structure
        return parsed?.user?.id || parsed?.access_token || null;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting userId from Supabase:', error);
    return null;
  }
};

// Add the createVoiceResponse function
export const createVoiceResponse = async (audioBlob: Blob, persona: string, personality?: string): Promise<Blob> => {
  console.log('Creating voice response for persona:', persona, 'with personality:', personality);
  
  // Generate a random session ID if we don't have one
  const sessionId = Math.random().toString(36).substring(2, 15);
  
  // Try multiple sources for userId
  let userId = localStorage.getItem('userId');
  
  if (!userId) {
    userId = getUserIdFromSupabase();
  }
  
  if (!userId) {
    userId = localStorage.getItem('authUserId');
  }
  
  console.log('=== VOICE SERVICE DEBUG ===');
  console.log('Final userId resolved:', userId);
  console.log('userId type:', typeof userId);
  console.log('All localStorage keys:', Object.keys(localStorage));
  
  return sendAudioRequest({
    audioBlob,
    persona,
    sessionId,
    userId: userId || undefined,
    messageType: 'voice',
    personality
  });
};

export const sendAudioRequest = async ({
  audioBlob,
  persona,
  sessionId,
  userId,
  messageType,
  personality
}: VoiceRequestParams): Promise<Blob> => {
  const formData = new FormData();
  formData.append('data', audioBlob, 'recording.wav');
  formData.append('assistant', persona);
  formData.append('sessionId', sessionId);
  formData.append('messageType', messageType);
  
  console.log('=== VOICE SERVICE PAYLOAD DEBUG ===');
  console.log('userId parameter received:', userId);
  console.log('userId type:', typeof userId);
  console.log('userId truthy check:', !!userId);
  console.log('personality parameter received:', personality);
  
  // ALWAYS add userId to FormData, even if it's null/undefined
  formData.append('userId', userId || '');
  console.log('✅ userId added to FormData:', userId || 'empty string');
  
  // Add personality as "voice" parameter if provided
  if (personality) {
    formData.append('voice', personality);
    console.log('✅ voice parameter added to FormData:', personality);
  }
  
  // Use the correct webhook URL for voice interactions
  const webhookUrl = 'https://n8n.k2innoventions.com/webhook/d665fbb3-e9a2-4176-ba1d-585beadbbec3';
  
  console.log('===== VOICE SERVICE =====');
  console.log('Preparing to send audio to voice webhook:', webhookUrl);
  console.log('Audio blob type:', audioBlob.type);
  console.log('Audio blob size:', audioBlob.size);
  console.log('Session ID:', sessionId);
  console.log('Persona being sent to webhook:', persona);
  console.log('User ID being sent:', userId || 'Empty string');
  console.log('Voice personality being sent:', personality || 'Not specified');
  
  try {
    console.log('Calling sendFormDataWithAuth...');
    
    // Create a dummy blob for testing if needed
    if (audioBlob.size === 0) {
      console.warn('Empty audio blob detected, creating dummy blob for testing');
      audioBlob = new Blob(['dummy audio data'], { type: 'audio/webm' });
      formData.set('data', audioBlob, 'recording.wav');
    }

    // Verify FormData contents before sending
    console.log('Final FormData contents before sending:');
    for (const pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[0] === 'data' ? '[Blob data]' : pair[1]}`);
    }
    
    const response = await sendFormDataWithAuth(
      webhookUrl,
      formData
    );
    
    console.log('Webhook response received!');
    console.log('Webhook response status:', response.status);
    console.log('Webhook response status text:', response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response from webhook:", errorText);
      throw new Error(`Server responded with status: ${response.status}`);
    }
    
    // Try to parse the response as JSON to see its structure
    try {
      const responseText = await response.clone().text();
      console.log('Response text:', responseText);
      try {
        const jsonResponse = JSON.parse(responseText);
        console.log('Response parsed as JSON:', jsonResponse);
      } catch (e) {
        console.log('Response is not JSON format');
      }
    } catch (e) {
      console.log('Could not clone and read response for debugging');
    }
    
    // Get the actual blob data
    const responseBlob = await response.blob();
    console.log('Response blob received, size:', responseBlob.size, 'type:', responseBlob.type);
    
    // Return dummy audio blob if the response is empty (for testing)
    if (responseBlob.size === 0) {
      console.warn('Empty response blob, creating dummy response for testing');
      return new Blob(['dummy response audio'], { type: 'audio/mp3' });
    }
    
    return responseBlob;
  } catch (error) {
    console.error("Critical error when calling webhook:", error);
    throw error;
  }
};
